import $t from '@Ravnur-Inc/l10n/$t';
import { Options, Vue, setup } from 'vue-class-component';

import PlaylistMediaRepository from '@/repositories/playlist-media-repository';
import useSecurityStore from '@/store/security';
import { Media$Type, Media$Details, Media, OrderableCheckableMedia } from '@/types/Media';
import PopupButton from '@/components/popup-button/popup-button';
import MediaPickerModal from '../media-picker-modal/media-picker-modal';
import { toOrderable } from '@/transformers/orderable';
import { toCheckable } from '@/transformers/checkable';
import ReorderingList from '@/components/reordering-list/reordering-list';
import MediaListSnippet from '../media-list-snippet/media-list-snippet';
import isEqual from '@Ravnur-Inc/nanoutils/isEqual';
import modalService from '@/helpers/modal.service';

import './playlist-items.scss';

const CN = 'playlist-items';

class Props {
  origin!: Media$Details;
  mediaType: Media$Type;
}

@Options({
  inject: {
    refreshPlayerSources: 'refreshPlayerSources',
  },
})
export default class PlaylistItems extends Vue.with(Props) {
  declare refreshPlayerSources: (() => void) | undefined;

  security = setup(() => useSecurityStore());

  loading = false;
  originCheckable: Array<OrderableCheckableMedia> = [];
  checkableList: Array<OrderableCheckableMedia> = [];
  columns = 3;

  get isLivePlaylist() {
    return (
      !!this.origin.playlistStreamingChannelIds?.length && this.security.isLivePlaylistsEnabled
    );
  }

  get repository() {
    return new PlaylistMediaRepository(this.origin.id);
  }

  get items(): Media[] {
    return this.checkableList.map((w) => w.entity);
  }

  set items(list: Media[]) {
    this.checkableList = toOrderable(toCheckable(list));
  }

  get isChanged() {
    return !isEqual(this.checkableList, this.originCheckable);
  }

  render() {
    return (
      <div class={CN} {...this.$attrs}>
        {this.loading && <spinner />}
        <div class={`${CN}__header`}>
          <PopupButton
            class={`${CN}__modal-btn`}
            color="primary"
            icon="add"
            mode="standard"
            text={$t('media', 'playlist-items__edit')}
          >
            <MediaPickerModal
              excluded={[this.origin]}
              process={this.append}
              queryParams={{ type: this.mediaType }}
              value={[]}
            />
          </PopupButton>
        </div>

        <ReorderingList
          localId
          v-slots={{
            item: ({ entity }: { entity: OrderableCheckableMedia }) => this.renderCard(entity),
          }}
          vModel={[this.checkableList, 'list']}
          onUpdate:list={this.handleDragEnd}
        />
        {this.items.length === 0 && <messages value={this.$t('media', 'playlist-items__empty')} />}
        <div class={`${CN}__actions`}>
          <r-button mode="frameless" onClick={this.cancel}>
            <l10n group="common" tkey="action__cancel" />
          </r-button>
          <r-button color="primary" disabled={!this.isChanged || this.loading} onClick={this.save}>
            <l10n group="common" tkey="action__save" />
          </r-button>
        </div>
      </div>
    );
  }

  private renderCard(wrapper: OrderableCheckableMedia) {
    const cn = [`${CN}__card`, `${CN}__card--1x${this.columns}`];
    const slots = {
      actions: () => (
        <r-button
          class={`${CN}__delete`}
          icon="trash"
          mode="frameless"
          onclick={() => this.removeItem(wrapper)}
        />
      ),
    };
    return (
      <MediaListSnippet key={wrapper.entity.id} class={cn} media={wrapper.entity} v-slots={slots} />
    );
  }

  cancel() {
    this.items = this.originCheckable.map((w) => w.entity);
  }

  async handleDragEnd(items: OrderableCheckableMedia[]) {
    this.items = items.map(({ entity }) => entity);
  }

  async load() {
    this.loading = true;

    try {
      const list = await this.repository.load();
      this.checkableList = toOrderable(toCheckable(list));
      this.originCheckable = this.checkableList;
    } catch (e) {
      this.$processException(e);
    }

    this.loading = false;
  }

  async append(list: Media[]) {
    this.items = [...list, ...this.items];
  }

  async save() {
    const confirmationModalProps = {
      closeLabel: '',
      confirmText: $t('common', 'action__ok'),
    };

    if (this.isLivePlaylist && this.items.length === 0) {
      modalService.confirm($t('media', 'playlist-items__live-info-empty'), confirmationModalProps);

      return;
    }

    if (this.isLivePlaylist) {
      modalService.confirm($t('media', 'playlist-items__live-info'), confirmationModalProps);
    }

    this.loading = true;

    try {
      await this.repository.saveMany(this.items);
      this.originCheckable = toOrderable(toCheckable(this.items));
      this.refreshPlayerSources?.();
    } catch (e) {
      this.$processException(e);
    } finally {
      this.loading = false;
    }
  }

  async removeItem(wrapper: OrderableCheckableMedia) {
    const finalItems = this.checkableList
      .filter((w) => w.order !== wrapper.order)
      .map((w) => w.entity);
    this.items = finalItems;
  }

  mounted() {
    this.load();
  }
}
